import React, { useState, useEffect, useReducer } from "react";
import {
  Button, IconButton, Paper, Table, TableBody, TableCell,
  TableHead, TableRow, Chip, Switch, Tooltip,
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, FormGroup, FormControlLabel, Checkbox,
} from "@mui/material";
import { Edit, DeleteOutline, Add, PlayArrow } from "@mui/icons-material";
import { toast } from "react-toastify";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import ConfirmationModal from "../../components/ConfirmationModal";
import api from "../../services/api";
import toastError from "../../errors/toastError";

const ALL_EVENTS = ["ticket.created", "ticket.updated", "ticket.closed", "message.received", "message.sent"];

const DEFAULTS = { name: "", url: "", events: [], secret: "", isActive: true };

const reducer = (state, action) => {
  switch (action.type) {
    case "LOAD": return action.payload;
    case "UPDATE": { const i = state.findIndex(r => r.id === action.payload.id); if (i !== -1) { const s = [...state]; s[i] = action.payload; return s; } return [action.payload, ...state]; }
    case "DELETE": return state.filter(r => r.id !== action.payload);
    default: return state;
  }
};

const Webhooks = () => {
  const [configs, dispatch] = useReducer(reducer, []);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState(DEFAULTS);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  useEffect(() => {
    api.get("/webhooks").then(({ data }) => { dispatch({ type: "LOAD", payload: data }); setLoading(false); }).catch(toastError);
  }, []);

  const openNew = () => { setSelected(null); setForm(DEFAULTS); setModalOpen(true); };
  const openEdit = (cfg) => {
    setSelected(cfg);
    setForm({ name: cfg.name, url: cfg.url, events: JSON.parse(cfg.events || "[]"), secret: cfg.secret || "", isActive: cfg.isActive });
    setModalOpen(true);
  };

  const handleSave = async () => {
    if (!form.name.trim() || !form.url.trim()) { toast.error("Preencha nome e URL."); return; }
    try {
      const { data } = selected
        ? await api.put(`/webhooks/${selected.id}`, form)
        : await api.post("/webhooks", form);
      dispatch({ type: "UPDATE", payload: data });
      toast.success(selected ? "Webhook atualizado." : "Webhook criado.");
      setModalOpen(false);
    } catch (err) { toastError(err); }
  };

  const handleDelete = async () => {
    try { await api.delete(`/webhooks/${deleteId}`); dispatch({ type: "DELETE", payload: deleteId }); toast.success("Removido."); } catch (err) { toastError(err); }
  };

  const handleTest = async (id) => {
    try {
      const { data } = await api.post(`/webhooks/${id}/test`);
      toast[data.ok ? "success" : "error"](`Resposta: HTTP ${data.status}`);
    } catch (err) { toastError(err); }
  };

  const toggleEvent = (ev) => setForm(f => ({
    ...f, events: f.events.includes(ev) ? f.events.filter(e => e !== ev) : [...f.events, ev]
  }));

  return (
    <MainContainer>
      <ConfirmationModal title="Excluir webhook?" open={confirmOpen} onClose={() => setConfirmOpen(false)} onConfirm={handleDelete}>
        O webhook será removido permanentemente.
      </ConfirmationModal>
      <Dialog open={modalOpen} onClose={() => setModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{selected ? "Editar webhook" : "Novo webhook"}</DialogTitle>
        <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: "12px !important" }}>
          <TextField label="Nome" size="small" fullWidth value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
          <TextField label="URL" size="small" fullWidth value={form.url} onChange={e => setForm(f => ({ ...f, url: e.target.value }))} placeholder="https://..." />
          <TextField label="Secret (opcional)" size="small" fullWidth value={form.secret} onChange={e => setForm(f => ({ ...f, secret: e.target.value }))} helperText="Enviado como X-Webhook-Signature (HMAC-SHA256)" />
          <div>
            <div style={{ marginBottom: 4, fontSize: "0.8rem", color: "gray" }}>Eventos (vazio = todos)</div>
            <FormGroup row>
              {ALL_EVENTS.map(ev => (
                <FormControlLabel key={ev} control={<Checkbox size="small" checked={form.events.includes(ev)} onChange={() => toggleEvent(ev)} />} label={<span style={{ fontSize: "0.8rem" }}>{ev}</span>} />
              ))}
            </FormGroup>
          </div>
          <FormControlLabel control={<Switch checked={form.isActive} onChange={e => setForm(f => ({ ...f, isActive: e.target.checked }))} color="primary" />} label="Ativo" />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setModalOpen(false)}>Cancelar</Button>
          <Button onClick={handleSave} variant="contained" color="primary">Salvar</Button>
        </DialogActions>
      </Dialog>
      <MainHeader>
        <Title>Webhooks outgoing</Title>
        <MainHeaderButtonsWrapper>
          <Button variant="contained" color="primary" startIcon={<Add />} onClick={openNew}>Novo webhook</Button>
        </MainHeaderButtonsWrapper>
      </MainHeader>
      <Paper variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Nome</TableCell>
              <TableCell>URL</TableCell>
              <TableCell>Eventos</TableCell>
              <TableCell align="center">Ativo</TableCell>
              <TableCell align="center">Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? <TableRowSkeleton columns={5} /> : configs.map(cfg => (
              <TableRow key={cfg.id} hover>
                <TableCell>{cfg.name}</TableCell>
                <TableCell style={{ maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}><a href={cfg.url} target="_blank" rel="noreferrer">{cfg.url}</a></TableCell>
                <TableCell>
                  {(JSON.parse(cfg.events || "[]").length === 0 ? ["todos"] : JSON.parse(cfg.events)).map(e => (
                    <Chip key={e} label={e} size="small" style={{ marginRight: 2, fontSize: "0.7rem" }} />
                  ))}
                </TableCell>
                <TableCell align="center"><Switch size="small" checked={cfg.isActive} onChange={() => { api.put(`/webhooks/${cfg.id}`, { ...cfg, events: JSON.parse(cfg.events), isActive: !cfg.isActive }).then(({ data }) => dispatch({ type: "UPDATE", payload: data })).catch(toastError); }} color="primary" /></TableCell>
                <TableCell align="center">
                  <Tooltip title="Testar"><IconButton size="small" onClick={() => handleTest(cfg.id)}><PlayArrow fontSize="small" /></IconButton></Tooltip>
                  <Tooltip title="Editar"><IconButton size="small" onClick={() => openEdit(cfg)}><Edit fontSize="small" /></IconButton></Tooltip>
                  <Tooltip title="Excluir"><IconButton size="small" onClick={() => { setDeleteId(cfg.id); setConfirmOpen(true); }}><DeleteOutline fontSize="small" /></IconButton></Tooltip>
                </TableCell>
              </TableRow>
            ))}
            {!loading && configs.length === 0 && (
              <TableRow><TableCell colSpan={5} align="center" style={{ color: "gray", padding: 24 }}>Nenhum webhook cadastrado.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>
    </MainContainer>
  );
};

export default Webhooks;
